const DUE_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export type DueDateValidationResult = 'valid' | 'invalid' | 'past';

const parseDateOnlyParts = (
	dateOnly: string,
): { year: number; month: number; day: number } | null => {
	if (!DUE_DATE_PATTERN.test(dateOnly)) {
		return null;
	}

	const [yearText, monthText, dayText] = dateOnly.split('-');
	const year = Number(yearText);
	const month = Number(monthText);
	const day = Number(dayText);
	const date = new Date(Date.UTC(year, month - 1, day));

	if (
		date.getUTCFullYear() !== year ||
		date.getUTCMonth() !== month - 1 ||
		date.getUTCDate() !== day
	) {
		return null;
	}

	return { year, month, day };
};

export const getLocalDateOnly = (date: Date = new Date()): string => {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');

	return `${year}-${month}-${day}`;
};

export const validateDueDate = (
	dateOnly: string,
	todayDateOnly: string = getLocalDateOnly(),
): DueDateValidationResult => {
	if (dateOnly === '') {
		return 'valid';
	}

	if (!parseDateOnlyParts(dateOnly)) {
		return 'invalid';
	}

	return dateOnly < todayDateOnly ? 'past' : 'valid';
};

export const encodeDueDateForMutation = (dateOnly: string): string => `${dateOnly}T00:00:00Z`;

export const extractDateOnlyFromUtcEpochMillis = (epochMillis: string): string | null => {
	const millis = Number(epochMillis);
	if (!Number.isFinite(millis)) {
		return null;
	}

	const date = new Date(millis);
	const year = date.getUTCFullYear();
	const month = String(date.getUTCMonth() + 1).padStart(2, '0');
	const day = String(date.getUTCDate()).padStart(2, '0');

	return `${year}-${month}-${day}`;
};

export const getNoonUtcDateFromDateOnly = (dateOnly: string): Date | null => {
	const parts = parseDateOnlyParts(dateOnly);
	if (!parts) {
		return null;
	}

	return new Date(Date.UTC(parts.year, parts.month - 1, parts.day, 12));
};

export const isDueDateOverdue = (
	dateOnly: string,
	todayDateOnly: string = getLocalDateOnly(),
): boolean => validateDueDate(dateOnly, todayDateOnly) === 'past';

type ApprovalExpirationInterval = {
	value: number;
	unit: string;
};

const formatDateOnly = (date: Date): string => {
	const year = date.getUTCFullYear();
	const month = String(date.getUTCMonth() + 1).padStart(2, '0');
	const day = String(date.getUTCDate()).padStart(2, '0');

	return `${year}-${month}-${day}`;
};

const addMonths = (date: Date, months: number): Date => {
	const originalDay = date.getUTCDate();
	date.setUTCDate(1);
	date.setUTCMonth(date.getUTCMonth() + months);
	const lastDayOfTargetMonth = new Date(
		Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0),
	).getUTCDate();
	date.setUTCDate(Math.min(originalDay, lastDayOfTargetMonth));

	return date;
};

export const getApprovalExpirationDate = (
	approvalTimestamp: string,
	interval: ApprovalExpirationInterval | null | undefined,
): string | null => {
	const approvalDateOnly = extractDateOnlyFromUtcEpochMillis(approvalTimestamp);
	if (
		!approvalDateOnly ||
		!interval ||
		!Number.isSafeInteger(interval.value) ||
		interval.value < 1
	) {
		return null;
	}

	const approvalDate = getNoonUtcDateFromDateOnly(approvalDateOnly);
	if (!approvalDate) {
		return null;
	}

	switch (interval.unit) {
		case 'WEEK':
			approvalDate.setUTCDate(approvalDate.getUTCDate() + interval.value * 7);
			break;
		case 'MONTH':
			addMonths(approvalDate, interval.value);
			break;
		case 'YEAR':
			addMonths(approvalDate, interval.value * 12);
			break;
		default:
			return null;
	}

	return formatDateOnly(approvalDate);
};

export const isApprovalExpirationWarning = (
	expirationDateOnly: string,
	todayDateOnly: string = getLocalDateOnly(),
): boolean => {
	const expirationDate = getNoonUtcDateFromDateOnly(expirationDateOnly);
	const todayDate = getNoonUtcDateFromDateOnly(todayDateOnly);
	if (!expirationDate || !todayDate) {
		return false;
	}

	const daysUntilExpiration =
		(expirationDate.getTime() - todayDate.getTime()) / (1000 * 60 * 60 * 24);

	return daysUntilExpiration <= 7;
};
